<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import viewport from '@/js/utils/useViewportAction.js';
  import responsive from '@/js/utils/responsive';
  import ProtoScroll from '../../portfolio/work/proto/Scroll.svelte';
  import ProtoMobileControls from '../../portfolio/work/proto/ScrollMobileControls.svelte';

  let loading = true;
  let grow = false;
  export let section;
  // export let workItem;

  const dispatch = createEventDispatcher();

  let sectionEl, isDesktop;
  let activeButton = 'mobile';
  let hasMobileImg = section.mobileImg;
  let hasDesktopImg = section.desktopImg;
  let windowWidth = 0;
  $: mobile = windowWidth <= responsive.s;
  let imagePos = 0;
  // let nobuttons = false;

  function handleScrollUp() {
    imagePos += 100;
  }

  function handleScrollDown() {
    imagePos -= 100;
  }

  onMount(() => {
    handleScreenSize();
  });

  function handleScreenSize() {
    windowWidth < responsive.m ? (isDesktop = false) : (isDesktop = true);
  }

  /**
   * Dispatch event if middle of section is in viewport
   */
  function handleActiveSection() {
    const sectionBox = sectionEl.getBoundingClientRect();
    const sectionBottom = sectionBox.bottom;
    const windowHeight = window.innerHeight;

    let middle = sectionBottom - sectionBox.height / 5;

    if (middle < windowHeight) {
      dispatch('showActive', section);
    }
  }
</script>

<svelte:window
  bind:innerWidth={windowWidth}
  on:resize={handleScreenSize}
  on:scroll={handleActiveSection}
/>

<section
  class="section"
  id={section.id}
  bind:this={sectionEl}
  class:grow
  use:viewport
  on:enterViewport={() => (grow = true)}
>
  <h6
    class="section__title"
    class:nobuttons={!(!isDesktop && hasDesktopImg && hasMobileImg) &&
      !(hasDesktopImg && section.desktopImg.notScrollable)}
  >
    {section.title}
  </h6>
  <p class="section__text text-small">{section.text}</p>
  <div class="section__images">
    {#if hasMobileImg}
      <div
        class={isDesktop
          ? 'section__image section__image-mobile'
          : (activeButton === 'mobile') | !hasDesktopImg
          ? 'section__image section__image-mobile only-image'
          : 'section__image section__image-mobile inactive'}
      >
        <ProtoScroll image={section.mobileImg} {loading} bind:imagePos />
      </div>
      {#if mobile}
        <ProtoMobileControls
          on:clickUp={handleScrollUp}
          on:clickDown={handleScrollDown}
        />
      {/if}
    {/if}

    {#if hasDesktopImg}
      {#if section.desktopImg.notScrollable}
        <div
          class={isDesktop
            ? 'section__image section__image-desktop'
            : (activeButton === 'desktop') | !hasMobileImg
            ? 'section__image section__image-desktop only-image'
            : 'section__image section__image-desktop inactive'}
          class:only-image={!hasMobileImg | (activeButton === 'desktop')}
          class:not-scrollable={section.desktopImg.notScrollable}
        >
          <img
            src={section.desktopImg.src}
            alt={section.desktopImg.alt}
            height={section.desktopImg.height}
            width={section.desktopImg.width}
          />
        </div>
      {:else}
        <div
          class={isDesktop
            ? 'section__image section__image-desktop'
            : (activeButton === 'desktop') | !hasMobileImg
            ? 'section__image section__image-desktop only-image'
            : 'section__image section__image-desktop inactive'}
          class:only-image={!hasMobileImg | (activeButton === 'desktop')}
        >
          <ProtoScroll image={section.desktopImg} {loading} bind:imagePos />
        </div>
        {#if mobile}
          <ProtoMobileControls
            on:clickUp={handleScrollUp}
            on:clickDown={handleScrollDown}
          />
        {/if}
      {/if}
    {/if}
  </div>

  {#if !isDesktop && hasDesktopImg && hasMobileImg}
    <div class="section__buttons">
      <button
        class="text-link text-small"
        class:active={activeButton === 'mobile'}
        on:click={() => (activeButton = 'mobile')}>Mobile</button
      >
      <button
        class={activeButton === 'desktop'
          ? 'text-link text-small active'
          : 'text-link text-small'}
        on:click={() => (activeButton = 'desktop')}>Desktop</button
      >
    </div>
  {/if}
</section>

<style lang="scss">
  @keyframes fade-up {
    0% {
      opacity: 0;
      transform: translateY(30px);
    }

    75% {
      opacity: 0.5;
    }

    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .section {
    padding: 8rem 0;
    border-top: 1px solid $light-border;
    text-align: center;
    display: flex;
    flex-direction: column;

    &.grow {
      animation: fade-up 1s ease-in-out backwards;
    }

    @include resp-max('m') {
      padding: 7rem 0;
    }

    @include resp-max('s') {
      padding: 0 0 5rem;
      border-top: none;
    }

    &__title {
      color: var(--royal);

      @include resp-max('m') {
        order: 3;
        margin-top: 3rem;
      }

      @include resp-max('s') {
        margin-top: 2rem;
      }

      &.nobuttons {
        @include resp-max('s') {
          margin-top: 13rem;
        }
      }
    }

    &__text {
      margin: 1rem auto 0;
      text-align: left;
      max-width: 70rem;
      color: var(--royal-light);

      @include resp-max('m') {
        order: 4;
      }
    }

    &__images {
      display: flex;
      flex-direction: row;
      gap: 1.8rem;
      justify-content: center;
      flex-wrap: nowrap;
      margin-top: 4rem;
      position: relative;

      @include resp-max('m') {
        order: 1;
      }
    }

    &__image {
      border-radius: 7px;
      overflow: hidden;
      position: relative;
      background: var(--grey-3);
      border: 2px solid var(--grey-3);

      &.inactive {
        display: none;
      }

      &-mobile {
        aspect-ratio: 34/59;
        width: 30%;

        &.only-image {
          width: 33rem;
          max-width: 100%;
        }
      }

      &-desktop {
        aspect-ratio: 95/59;
        width: 70%;

        &.only-image {
          width: 100%;
          max-width: 115rem;
        }

        &.not-scrollable {
          aspect-ratio: unset;
          max-width: 115rem;

          img {
            width: 100%;
          }
        }
      }
    }

    &__buttons {
      margin-top: 3rem;

      @include resp-max('m') {
        order: 2;
      }

      @include resp-max('s') {
        margin-top: 15rem;
      }
    }

    .text-link {
      margin: 0 auto;
      color: var(--grey);
      transition: all 0.25s;

      &:not(:last-child) {
        margin-right: 3rem;
      }

      &::after {
        opacity: 0;
      }
      &:hover {
        color: var(--royal-light);
        &::after {
          opacity: 1;
        }
      }

      &.active {
        color: var(--royal-light);
        &::after {
          opacity: 1;
        }
      }
    }

    // .proto-wrap {
    //   width: 300px;
    //   aspect-ratio: 1/2;
    //   position: relative;
    // }
  }
</style>
